'use client'

import * as React from 'react'
import { useEffect, useRef } from 'react'
import { cn } from '../../../../../lib/utils'

import { type DraggableProps } from '../../../DataView/Kanban'
import { useDrop } from 'react-dnd'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { motion } from 'framer-motion'
import { buildKanbanTileLayout } from './layout'
import { KanbanFooterField, KanbanTileField, KanbanTileHeader } from './fields'
import type { KanbanTileComponent } from './tileTypes'

export type { KanbanTileComponent } from './tileTypes'

export type DragItem = {
  id: string
  index: number
  sectionId: string
  sectionIndex: number
  subsectionId: string
  type: 'Item'
}

export type KanbanTileProps = {
  id: string
  height?: number
  isSelected?: boolean
  title?: string
  description?: string
  className?: string
  draggable?: DraggableProps
  handleClick?: () => void
  components?: KanbanTileComponent[]
}

export type KanbanTileContentProps = Pick<
  KanbanTileProps,
  'id' | 'className' | 'title' | 'description' | 'components' | 'isSelected'
> & {
  dimContent?: boolean
  interactive?: boolean
  isDraggingVisual?: boolean
  accentColor?: string
  onClick?: (e: React.MouseEvent) => void
  innerRef?: React.Ref<HTMLDivElement>
}

export const KanbanTileContent = (props: KanbanTileContentProps) => {
  const {
    id,
    className,
    title,
    description,
    components,
    isSelected,
    dimContent,
    interactive = false,
    isDraggingVisual = false,
    accentColor,
    onClick,
    innerRef,
  } = props

  return (
    <div
      ref={innerRef}
      className={cn(
        'flex flex-col px-2.5 py-2 rounded-md',
        interactive && 'cursor-grab active:cursor-grabbing',
        !accentColor && 'bg-card border border-border/50',
        accentColor && 'border',
        interactive && 'hover:border-border/70 transition-all duration-150',
        isDraggingVisual &&
          'border-dashed border-2 border-primary/60 opacity-50',
        isSelected && 'bg-primary/10 border-primary/40 ring-1 ring-primary/30',
        className,
      )}
      style={
        accentColor && !isSelected
          ? {
              backgroundColor: `color-mix(in srgb, ${accentColor} 4%, hsl(var(--card)))`,
              borderColor: `color-mix(in srgb, ${accentColor} 10%, hsl(var(--border)))`,
            }
          : undefined
      }
      onClick={onClick}
    >
      <div className={cn('flex flex-col gap-1', dimContent && 'opacity-30')}>
        {components && components.length > 0 ? (
          (() => {
            const tileLayout = buildKanbanTileLayout(components)

            return (
              <>
                {tileLayout.header.map((component, index) => (
                  <KanbanTileHeader
                    key={`header-${index}`}
                    component={component}
                    index={index}
                    tileId={id}
                  />
                ))}

                {tileLayout.rows.map((row) =>
                  row.kind === 'full' ? (
                    <KanbanTileField key={row.key} component={row.component} />
                  ) : (
                    <div key={row.key} className="grid grid-cols-2 gap-1">
                      {row.components.map((component, index) => (
                        <KanbanTileField
                          key={`${row.key}-${index}`}
                          component={component}
                        />
                      ))}
                    </div>
                  ),
                )}

                {tileLayout.footer.length > 0 && (
                  <div className="flex flex-col gap-1 pt-0.5 border-t border-border/30">
                    {tileLayout.footer.map((component, index) => (
                      <KanbanFooterField
                        key={`footer-${index}`}
                        component={component}
                        index={index}
                        tileId={id}
                      />
                    ))}
                  </div>
                )}
              </>
            )
          })()
        ) : (
          <div className="flex flex-col justify-center overflow-hidden w-full">
            <div className="text-sm font-bold">{title}</div>
            <div className="text-sm text-muted-foreground">{description}</div>
          </div>
        )}
      </div>
    </div>
  )
}

export const KanbanTile = (props: KanbanTileProps) => {
  const {
    id,
    className,
    draggable,
    title,
    description,
    handleClick: _unusedHandleClick,
    isSelected,
    components,
  } = props
  void _unusedHandleClick

  const {
    handleMoveItem,
    handleMoveItems,
    handleSelect,
    handleIsSelected,
    handleDeselectAll,
    onHeight,
    isSectionDraggingOver,
    draggingItemId,
    onDragEnd,
  } = draggable ?? {}

  const itemRef = useRef<HTMLDivElement>(null)

  // Store onDragEnd in a ref to avoid recreating useDrag spec on every render
  const onDragEndRef = useRef(onDragEnd)
  onDragEndRef.current = onDragEnd

  // drag handling
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: 'Item',
      item: {
        id,
        index: draggable?.dropZone?.index,
        sectionId: draggable?.section?.id,
        sectionIndex: draggable?.section?.index,
        subsectionId: draggable?.subsection?.id,
        title,
        description,
        type: 'Item',
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: () => {
        // Flush pending section changes to persistence
        onDragEndRef.current?.()
      },
    }),
    [
      id,
      draggable?.dropZone?.index,
      draggable?.section?.id,
      draggable?.section?.index,
      draggable?.subsection?.id,
      title,
      description,
    ],
  )

  //drop handling
  const [{ getItem }, drop] = useDrop<DragItem, void, { getItem: DragItem }>({
    accept: 'Item',
    collect: (monitor) => ({
      getItem: monitor.getItem(),
    }),
    hover(item, monitor) {
      if (!itemRef.current) return

      // drag** is source entity and hover** is target entity
      const dragIndex = item.index
      const dragSubsectionId = item.subsectionId
      const hoverIndex = draggable?.dropZone?.index ?? 0
      const hoverSubsectionId = draggable?.subsection?.id
      const dragSectionId = draggable?.section?.id
      const hoverSectionId = item.sectionId

      // Don't replace items with themselves in the same section
      const isSameDroppableLocation =
        dragIndex === hoverIndex &&
        dragSubsectionId === hoverSubsectionId &&
        dragSectionId === hoverSectionId

      if (isSameDroppableLocation) return

      // Determine rectangle on screen
      const hoverBoundingRect = itemRef.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      // Calculate new index based on mouse position
      let destinationIndex = hoverIndex

      if (hoverClientY >= hoverMiddleY) destinationIndex = hoverIndex + 1

      // Only execute if position change is needed or if the item is the first item in the section
      if (
        dragIndex !== destinationIndex &&
        !(dragIndex === 0 && destinationIndex === 0) &&
        dragIndex !== undefined &&
        destinationIndex !== undefined
      ) {
        handleMoveItem?.(
          item.id,
          hoverSectionId,
          hoverSubsectionId || '',
          destinationIndex,
        )
        item.index = destinationIndex
        item.sectionId = hoverSectionId
        item.subsectionId = hoverSubsectionId ?? ''
      }
    },
    drop(item, monitor) {
      if (
        draggable?.selectedItemIds?.length &&
        draggable?.selectedItemIds?.includes(item.id)
      ) {
        const hoverIndex = draggable?.dropZone?.index ?? 0
        const hoverSectionId = item.sectionId
        const hoverSubsectionId = draggable?.subsection?.id
        const hoverBoundingRect = itemRef.current?.getBoundingClientRect()
        const clientOffset = monitor.getClientOffset()

        if (!hoverBoundingRect || !clientOffset) return

        const hoverClientY = clientOffset.y - hoverBoundingRect.top
        const hoverMiddleY = hoverBoundingRect.height / 2

        let destinationIndex = hoverIndex

        if (hoverClientY >= hoverMiddleY) destinationIndex = hoverIndex + 1

        handleMoveItems?.(
          draggable?.selectedItemIds ?? [],
          hoverSectionId,
          hoverSubsectionId || '',
          destinationIndex,
        )
        draggable?.selectedItemIds.forEach((id, index) => {
          item.index = destinationIndex + index
          item.sectionId = hoverSectionId
          item.subsectionId = hoverSubsectionId ?? ''
        })
      }
    },
  })

  // Hide basic preview and replace with custom preview in CustomDragLayer component
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  // calculate height of the item and pass it to the parent component
  // Recalculate when components change (e.g., when fields are added/removed)
  useEffect(() => {
    const ITEM_GAP = 2
    if (!itemRef.current || !onHeight) return

    const calculateHeight = () => {
      if (itemRef.current && onHeight) {
        const height = itemRef.current.getBoundingClientRect().height + ITEM_GAP
        onHeight(id, height + ITEM_GAP)
      }
    }

    // Calculate immediately
    calculateHeight()

    // Also observe resize changes for more reliable updates
    const resizeObserver = new ResizeObserver(() => {
      calculateHeight()
    })

    resizeObserver.observe(itemRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [id, onHeight, components])

  // Attach drag & drop refs and store node locally.
  const handleRef = (node: HTMLDivElement | null) => {
    itemRef.current = node
    drop(node)
    drag(node)
  }

  // Check if the item is being dragged or if the section is being dragged over for visual feedback
  const isItemAndSectionDragging =
    isDragging || (isSectionDraggingOver && draggingItemId === id)
  const accentColor = draggable?.section?.color

  return (
    <motion.div
      ref={handleRef}
      initial={{ y: draggable?.dropZone?.y }}
      animate={{ y: draggable?.dropZone?.y }}
      transition={{
        type: 'tween',
        duration: 0.15,
        ease: 'easeInOut',
      }}
      className={cn('absolute w-full px-0', 'kanban-tile', className)}
    >
      <KanbanTileContent
        id={id}
        title={title}
        description={description}
        components={components}
        isSelected={isSelected}
        accentColor={accentColor}
        interactive
        isDraggingVisual={isItemAndSectionDragging}
        dimContent={isItemAndSectionDragging}
        innerRef={itemRef}
        onClick={(e) => {
          const isMultiSelected = draggable?.selectedItemIds?.length
          const isClickedSelectedItem = draggable?.selectedItemIds?.includes(id)
          const isKeyDown = e.shiftKey || e.ctrlKey || e.metaKey

          if (e.shiftKey) {
            isMultiSelected
              ? handleSelect && handleSelect(id)
              : handleIsSelected && handleIsSelected(id, !isSelected)
            return
          }

          if (e.metaKey || e.ctrlKey) {
            handleIsSelected && handleIsSelected(id, !isSelected)
            return
          }

          if (isMultiSelected && !isClickedSelectedItem && !isKeyDown) {
            handleDeselectAll && handleDeselectAll()
            return
          }
        }}
      />
    </motion.div>
  )
}
